import { makeAutoObservable } from 'mobx';
import {successAlert, refresh} from 'shared/tools';
import {t} from 'shared/core';
import {endpoints, types, auth} from 'main/tools';
import {DomainStore} from 'shared/store';
import {User} from 'main/stores/users';
import {RecoveryCode} from 'shared/stores/recovery_codes';

class UserSettingsState {
  store = new DomainStore();

  user;
  recoveryCode = new RecoveryCode();
  recoveryCodeModalOpen = false;
  modalOpen = false;
  calendarDisconnectModalOpen = false;
  meetingDisconnectModalOpen = false;
  disconnectError = null;
  errors = {};
  passwordValues = {
    currentPassword: '',
    password: '',
    passwordConfirmation: ''
  };

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  async load() {
    await this.store._compose([
      endpoints.USER.with(auth.user.id)
    ]);

    this.user = new User(this.store._getSingle(types.USER_DETAIL));
  }

  showModal() {
    this.modalOpen = true;
  }

  hideModal() {
    this.modalOpen = false;
  }

  async payrollUpdateEmailNotification(value) {
    await this.updateUser('email_notification', {payrollUpdateEmailNotification: value});
  }

  async benefitUpdateEmailNotification(value) {
    await this.updateUser('email_notification', {benefitUpdateEmailNotification: value});
  }

  async updateEmailNotification(value) {
    await this.updateUser('email_notification', {recruitingEmailNotification: value});
  }

  async updateTimeOffApprovedNotification(value) {
    await this.updateUser('email_notification', {timeOffApprovedNotification: value});
  }

  async updateBirthdaySharedInternally(value) {
    await this.updateUser('privacy_setting', {birthdaySharedInternally: value});
  }

  async saveAccountPreferences() {
    const {model, errors} = await this.store.patch(this.user.pick(['email', 'emailNotificationType', 'preferredLocale']));
    this.errors = errors;

    if (model) {
      this.user.merge(model);
      successAlert(t('user_settings.account_preferences.Account preferences saved.'));
    }
  }

  async updatePassword() {
    const {model, errors} = await this.store.patch(
      this.user.link('updatePassword'),
      types.USER_DETAIL,
      this.passwordValues
    );
    this.errors = errors;

    if (model) {
      successAlert(t('user_settings.password.Password updated'));
    }
  }

  async updateEmailUnsubscribe(value) {
    if (value === this.user.unsubscribedFromEmails) return null;

    this.user.merge({unsubscribedFromEmails: value});

    const {model, errors} = await this.store.patch(
      this.user.link('updateEmailSubscription'),
      types.USER_DETAIL,
      this.user
    );
    this.errors = errors;

    if (model) {
      successAlert(t('user_settings.email_preferences.Email preferences saved.'));
    }
  }

  async updateUser(updateType, updateObject) {
    this.user.merge(updateObject);
    const {model, errors} = await this.store.patch(this.user.pick(Object.keys(updateObject)));
    this.errors = errors;

    if (model) {
      switch (updateType) {
        case 'email_notification':
          successAlert(t('user_settings.email_preferences.Email preferences saved.'));
          break;
        case 'privacy_setting':
          successAlert(t('user_settings.privacy_settings.Privacy settings saved.'));
          break;
        default:
      }
    }
  }

  async disconnectSlackAccount() {
    const {model} = await this.store.post(
      endpoints.SLACK.DISCONNECT_USER,
      types.USER_DETAIL
    );

    if (model) {
      this.user.merge(model);
      this.hideModal();
      successAlert(t('user_settings.slack.Slack account disconnected.'));
    }
  }

  showCalendarDisconnectModal() {
    this.disconnectError = null;
    this.calendarDisconnectModalOpen = true;
  }

  hideCalendarDisconnectModal() {
    this.calendarDisconnectModalOpen = false;
  }

  showMeetingDisconnectModal() {
    this.disconnectError = null;
    this.meetingDisconnectModalOpen = true;
  }

  hideMeetingDisconnectModal() {
    this.meetingDisconnectModalOpen = false;
  }

  async disconnectCalendarApp() {
    const {model, errors} = await this.store.post(
      endpoints.APP_CONNECTIONS.DISCONNECT.with('calendar'),
      types.USER_DETAIL
    );

    if (model) {
      this.user.merge(model);
      this.hideCalendarDisconnectModal();
      successAlert(t('user_settings.scheduling_integrations.Calendar app disconnected.'));
    } else {
      this.disconnectError = errors.base;
    }
  }

  async disconnectMeetingApp() {
    const {model, errors} = await this.store.post(
      endpoints.APP_CONNECTIONS.DISCONNECT.with('meeting'),
      types.USER_DETAIL
    );

    if (model) {
      this.user.merge(model);
      this.hideMeetingDisconnectModal();
      successAlert(t('user_settings.scheduling_integrations.Meeting app disconnected.'));
    } else {
      this.disconnectError = errors.base;
    }
  }

  get slackAuthenticityToken() {
    return document.getElementsByName('csrf-token')[0].content;
  }

  async disconnectMFA() {
    await this.store.post(
      endpoints.MFA.DISCONNECT,
      types.USER_DETAIL
    );

    await refresh();
  }

  async generateRecoveryCode() {
    this.store.invalidate(types.RECOVERY_CODE);

    await this.store.post(endpoints.RECOVERY_CODES, types.RECOVERY_CODE);
    this.recoveryCode.merge(
      this.store._getSingle(types.RECOVERY_CODE)
    );
    this.recoveryCodeModalOpen = true;
  }

  closeRecoveryCodeModal() {
    this.recoveryCodeModalOpen = false;
  }

  get showUnsubscribeToggle() {
    return this.user.hasLink('updateEmailSubscription');
  }

  get showRecruitingToggle() {
    return !this.user.unsubscribedFromEmails && auth.moduleEnabled('ats');
  }

  get showPayrollToggle() {
    return !this.user.unsubscribedFromEmails && (auth.hasAccess('::PAYROLL') || auth.hasAccess('::PAYROLL_UPDATES_REPORT'));
  }

  get showBenefitToggle() {
    return !this.user.unsubscribedFromEmails && (auth.hasAccess('::MANAGE_BENEFITS') || auth.hasAccess('::BENEFIT_UPDATES_REPORT'));
  }

  get showTimeOffApprovedToggle() {
    return !this.user.unsubscribedFromEmails;
  }

  get mfaRequired() {
    return auth.company.mfaSmsEnabled || auth.company.mfaAuthenticatorEnabled;
  }

  get notificationEmailOptions() {
    const options = [
      {
        value: 'login',
        label: `${this.user.email} (${t('user_settings.account_preferences.Login Email')})`
      }
    ];

    if (this.user.employee) {
      options.push({
        value: 'work',
        label: this.user.employee.workEmail ? `${this.user.employee.workEmail} (${t('user_settings.account_preferences.Work Email')})` : t('user_settings.account_preferences.Work Email (not set)'),
        disabled: !this.user.employee.workEmail
      });
    }

    return options;
  }
}

export default UserSettingsState;
